import { Button, Input, NativeSelect, VStack } from '@chakra-ui/react'
import React from 'react'

import { Field, Modal } from '@mangovoice/mango-ui'

import type { SwitchboardTranslateFn } from '../../types'
import { text } from '../../utils/text'

export interface EditSwitchboardMetadataDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean
  /** Callback when dialog should close */
  onClose: () => void
  /** Current name value */
  name: string
  /** Callback when name changes */
  setName: (value: string) => void
  /** Current number value */
  number: string
  /** Callback when number changes */
  setNumber: (value: string) => void
  /** Current omit from directory value */
  omitFromDirectory: string
  /** Callback when omit from directory changes */
  setOmitFromDirectory: (value: string) => void
  /** Callback when form is submitted */
  onSubmit: () => Promise<void>
  /** Whether submit is in progress */
  isPending: boolean
  /** Optional Field component override */
  FieldComponent?: typeof Field
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
}

/**
 * Dialog for editing switchboard metadata (name, number, omit from directory)
 */
export const EditSwitchboardMetadataDialog =
  React.memo<EditSwitchboardMetadataDialogProps>(
    ({
      isOpen,
      onClose,
      name,
      setName,
      number,
      setNumber,
      omitFromDirectory,
      setOmitFromDirectory,
      onSubmit,
      isPending,
      FieldComponent = Field,
      t,
    }) => {
      const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        await onSubmit()
      }

      return (
        <Modal
          open={isOpen}
          onOpenChange={(details) => !details.open && onClose()}
          title={text(t, 'switchboard.list.edit.title', 'Edit Switchboard')}
          maxWidth="500px"
          footer={
            <>
              <Button
                type="button"
                variant="outline"
                colorPalette="neutral"
                onClick={onClose}
                px={5}
                py={2.5}
                borderRadius="lg"
                borderColor="border.default"
                _hover={{
                  borderColor: 'border.emphasized',
                  bg: 'bg.subtle',
                }}
              >
                {text(t, 'switchboard.list.edit.cancel', 'Cancel')}
              </Button>
              <Button
                type="submit"
                form="edit-switchboard-metadata-form"
                colorPalette="brand"
                loading={isPending}
                disabled={!name.trim() || !number.trim()}
                px={5}
                py={2.5}
                borderRadius="lg"
              >
                {text(t, 'switchboard.list.edit.submit', 'Update Switchboard')}
              </Button>
            </>
          }
        >
          <form id="edit-switchboard-metadata-form" onSubmit={handleFormSubmit}>
            <VStack gap={4} align="stretch">
              <FieldComponent.Root required>
                <FieldComponent.Label>
                  {text(t, 'switchboard.list.edit.nameLabel', 'Name')}
                </FieldComponent.Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={text(
                    t,
                    'switchboard.list.edit.namePlaceholder',
                    'e.g., Switchboard - 7074',
                  )}
                  borderColor="border.default"
                  pl={3}
                  _focusVisible={{
                    borderColor: 'border.emphasized',
                    boxShadow:
                      '0 0 0 1px var(--chakra-colors-border-emphasized)',
                  }}
                />
              </FieldComponent.Root>

              <FieldComponent.Root required>
                <FieldComponent.Label>
                  {text(t, 'switchboard.list.edit.numberLabel', 'Number')}
                </FieldComponent.Label>
                <Input
                  type="number"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  placeholder={text(
                    t,
                    'switchboard.list.edit.numberPlaceholder',
                    'e.g., 7074',
                  )}
                  min={0}
                  max={99999}
                  borderColor="border.default"
                  pl={3}
                  _focusVisible={{
                    borderColor: 'border.emphasized',
                    boxShadow:
                      '0 0 0 1px var(--chakra-colors-border-emphasized)',
                  }}
                />
              </FieldComponent.Root>

              <FieldComponent.Root>
                <FieldComponent.Label>
                  {text(
                    t,
                    'switchboard.list.edit.omitFromDirectoryLabel',
                    'Omit From Directory',
                  )}
                </FieldComponent.Label>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={omitFromDirectory}
                    onChange={(e) => setOmitFromDirectory(e.target.value)}
                    borderColor="border.default"
                    pl={3}
                    _focusVisible={{
                      borderColor: 'border.emphasized',
                      boxShadow:
                        '0 0 0 1px var(--chakra-colors-border-emphasized)',
                    }}
                  >
                    <option value="no">
                      {text(t, 'switchboard.list.edit.omitNo', 'No')}
                    </option>
                    <option value="yes">
                      {text(t, 'switchboard.list.edit.omitYes', 'Yes')}
                    </option>
                  </NativeSelect.Field>
                </NativeSelect.Root>
              </FieldComponent.Root>
            </VStack>
          </form>
        </Modal>
      )
    },
  )

EditSwitchboardMetadataDialog.displayName = 'EditSwitchboardMetadataDialog'
